// this is the same as InstantSearch' connectRange,
// except it uses a separate attribute for min than for max
// API is the same as connectRange, except
// attribute -> attributes: { min, max }

// ponyfill for Number#isFinite
function isFiniteNumber(value) {
  return typeof value === 'number' && isFinite(value);
}

// ponyfill for Array#find
function find(items, predicate) {
  if (!Array.prototype.find) {
    return items.filter(predicate)[0];
  }

  return items.find(predicate);
}

const noop = () => {};

function _getCurrentRange(
  stats,
  { precision, hasMinBound, minBound, hasMaxBound, maxBound }
) {
  let min;
  if (hasMinBound) {
    min = minBound;
  } else if (isFiniteNumber(stats.min)) {
    min = stats.min;
  } else {
    min = 0;
  }

  let max;
  if (hasMaxBound) {
    max = maxBound;
  } else if (isFiniteNumber(stats.max)) {
    max = stats.max;
  } else {
    max = 0;
  }

  return {
    min: Math.floor(min),
    max: Math.ceil(max),
  };
}

function _getCurrentRefinement(helper, attributes) {
  const [minValue] = helper.getNumericRefinement(attributes.min, '<=') || [];

  const [maxValue] = helper.getNumericRefinement(attributes.max, '>=') || [];

  const min = isFiniteNumber(minValue) ? minValue : -Infinity;
  const max = isFiniteNumber(maxValue) ? maxValue : Infinity;

  return [max, min];
}

export default function connectCustomRange(renderFn, unmountFn = noop) {
  return (widgetParams = {}) => {
    const {
      attributes,
      min: minBound,
      max: maxBound,
      precision = 0,
    } = widgetParams;

    const hasMinBound = isFiniteNumber(minBound);
    const hasMaxBound = isFiniteNumber(maxBound);

    if (!attributes || !attributes.min || !attributes.max) {
      throw new Error(
        'The `attributes` option is required, and needs a `min` and `max`'
      );
    }

    if (hasMinBound && hasMaxBound && minBound > maxBound) {
      throw new Error("The `max` option can't be lower than `min`.");
    }

    const formatToNumber = v => Number(Number(v).toFixed(precision));

    const rangeFormatter = {
      from: v => v,
      to: v => formatToNumber(v).toLocaleString(),
    };

    return {
      $$type: 'custom-range',

      _refine(helper, currentRange) {
        // eslint-disable-next-line complexity
        return ([nextMin, nextMax] = []) => {
          const { min: currentRangeMin, max: currentRangeMax } = currentRange;

          const [min] = helper.getNumericRefinement(attributes.min, '<=') || [];
          const [max] = helper.getNumericRefinement(attributes.max, '>=') || [];

          const isResetMin = nextMin === undefined || nextMin === '';
          const isResetMax = nextMax === undefined || nextMax === '';

          const nextMinAsNumber = !isResetMin ? parseFloat(nextMin) : undefined;
          const nextMaxAsNumber = !isResetMax ? parseFloat(nextMax) : undefined;

          let newNextMin;
          if (!hasMinBound && currentRangeMin === nextMinAsNumber) {
            newNextMin = undefined;
          } else if (hasMinBound && isResetMin) {
            newNextMin = minBound;
          } else {
            newNextMin = nextMinAsNumber;
          }

          let newNextMax;
          if (!hasMaxBound && currentRangeMax === nextMaxAsNumber) {
            newNextMax = undefined;
          } else if (hasMaxBound && isResetMax) {
            newNextMax = maxBound;
          } else {
            newNextMax = nextMaxAsNumber;
          }

          const isResetNewNextMin = newNextMin === undefined;
          const isValidNewNextMin = isFiniteNumber(newNextMin);
          const isValidMinCurrentRange = isFiniteNumber(currentRangeMin);
          const isGreaterThanCurrentRange =
            isValidMinCurrentRange && currentRangeMin <= newNextMin;
          const isMinValid =
            isResetNewNextMin ||
            (isValidNewNextMin &&
              (!isValidMinCurrentRange || isGreaterThanCurrentRange));

          const isResetNewNextMax = newNextMax === undefined;
          const isValidNewNextMax = isFiniteNumber(newNextMax);
          const isValidMaxCurrentRange = isFiniteNumber(currentRangeMax);
          const isLowerThanRange =
            isValidMaxCurrentRange && currentRangeMax >= newNextMax;
          const isMaxValid =
            isResetNewNextMax ||
            (isValidNewNextMax &&
              (!isValidMaxCurrentRange || isLowerThanRange));

          const hasMinChange = min !== newNextMin;
          const hasMaxChange = max !== newNextMax;

          if ((hasMinChange || hasMaxChange) && (isMinValid && isMaxValid)) {
            
            helper.removeNumericRefinement(attributes.min);
            helper.removeNumericRefinement(attributes.max); 

            if (isValidNewNextMin) {
                // helper.addNumericRefinement(
                //   attributes.min,
                //   '>=',
                //   formatToNumber(newNextMin)
                // );
                helper.addNumericRefinement(
                  attributes.max,
                  '>=',
                  formatToNumber(newNextMin)
                );
                
            }

            if (isValidNewNextMax) {
                // helper.addNumericRefinement(
                //   attributes.max,
                //   '<=',
                //   formatToNumber(newNextMax)
                // );
              
                helper.addNumericRefinement(
                  attributes.min,
                  '<=',
                  formatToNumber(newNextMax)
                );
            }

            helper.search();
          }
        }
      },

      init({ helper, instantSearchInstance }) {
        const stats = {};
        const currentRange = _getCurrentRange(stats, {
          precision,
          hasMinBound,
          minBound,
          hasMaxBound,
          maxBound,
        });
        const start = _getCurrentRefinement(helper, attributes);

        renderFn(
          {
            // On first render pass an empty range
            // to be able to bypass the validation
            // related to it
            refine: this._refine(helper, {}),
            format: rangeFormatter,
            range: currentRange,
            widgetParams: {
              ...widgetParams,
              precision,
            },
            start,
            instantSearchInstance,
          },
          true
        );
      },

      render({ results, helper, instantSearchInstance }) {
        const facetsFromResults = results.disjunctiveFacets || [];
        const facetMin = find(
          facetsFromResults,
          facetResult => facetResult.name === attributes.min
        ) || { stats: {} };
        const facetMax = find(
          facetsFromResults,
          facetResult => facetResult.name === attributes.max
        ) || { stats: {} };

        const stats = {
          min: facetMin.stats.min,
          max: facetMax.stats.max,
        };

        const currentRange = _getCurrentRange(stats, {
          precision,
          hasMinBound,
          minBound,
          hasMaxBound,
          maxBound,
        });
        const start = _getCurrentRefinement(helper, attributes);

        renderFn(
          {
            refine: this._refine(helper, currentRange),
            format: rangeFormatter,
            range: currentRange,
            widgetParams: {
              ...widgetParams,
              precision,
            },
            start,
            instantSearchInstance,
          },
          false
        );
      },

      dispose({ state }) {
        unmountFn();

        const stateWithoutDisjunctive = state
          .removeDisjunctiveFacet(attributes.min)
          .removeDisjunctiveFacet(attributes.max);

        // can not use setQueryParameters || removeNumericRefinement, because
        // they both keep the old value. This isn't immutable, but it is fine
        // since it's already a copy.
        stateWithoutDisjunctive.numericRefinements = {
          ...state.numericRefinements,
          [attributes.min]: undefined,
          [attributes.max]: undefined,
        };

        return stateWithoutDisjunctive;
      },

      getWidgetState(uiState, { searchParameters }) {
        const { '<=': min = [] } = searchParameters.getNumericRefinements(
          attributes.min
        );
        const { '>=': max = [] } = searchParameters.getNumericRefinements(
          attributes.max
        );

        if (min.length === 0 && max.length === 0) {
          return uiState;
        }

        return {
          ...uiState,
          range: {
            ...uiState.range,
            [`${attributes.min}_${attributes.max}`]: `${min}:${max}`,
          },
        };
      },

      getWidgetSearchParameters(searchParameters, { uiState }) {
        let widgetSearchParameters = searchParameters
          .addDisjunctiveFacet(attributes.min)
          .addDisjunctiveFacet(attributes.max)
          .setQueryParameters({
            numericRefinements: {
              ...searchParameters.numericRefinements,
              [attributes.min]: {},
              [attributes.max]: {},
            },
          });

        if (hasMinBound) {
          widgetSearchParameters = widgetSearchParameters.addNumericRefinement(
            attributes.min,
            '<=',
            minBound
          );
        }

        if (hasMaxBound) {
          widgetSearchParameters = widgetSearchParameters.addNumericRefinement(
            attributes.max,
            '>=',
            maxBound
          );
        }

        const value =
          uiState.range && uiState.range[`${attributes.min}_${attributes.max}`];

        if (!value || value.indexOf(':') === -1) {
          return widgetSearchParameters;
        }

        const [lowerBound, upperBound] = value.split(':').map(parseFloat);

        if (isFiniteNumber(lowerBound)) {
          widgetSearchParameters = widgetSearchParameters.addNumericRefinement(
            attributes.min,
            '<=',
            lowerBound
          );
        }

        if (isFiniteNumber(upperBound)) {
          widgetSearchParameters = widgetSearchParameters.addNumericRefinement(
            attributes.max,
            '>=',
            upperBound
          );
        }

        return widgetSearchParameters;
      },
    };
  };
}
  